// Gestión de consentimiento de cookies (RGPD / LSSI).
// - Las cookies/píxeles no esenciales NO se cargan hasta que el usuario acepte.
// - El usuario puede aceptar, rechazar o configurar por categorías.
// - Puede cambiar de opinión en cualquier momento desde el footer ("Configurar cookies").

export type ConsentCategories = {
  necessary: true; // siempre true, no se puede desactivar
  analytics: boolean;
  marketing: boolean;
};

export type ConsentState = {
  categories: ConsentCategories;
  decided: boolean; // true cuando el usuario ya tomó una decisión
  timestamp: number;
  version: number;
};

const STORAGE_KEY = "vibra_consent_v1";
const CURRENT_VERSION = 1;
const CHANGE_EVENT = "vibra:consent-change";
const OPEN_EVENT = "vibra:consent-open";

const DEFAULT_STATE: ConsentState = {
  categories: { necessary: true, analytics: false, marketing: false },
  decided: false,
  timestamp: 0,
  version: CURRENT_VERSION,
};

export function getConsent(): ConsentState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as ConsentState;
    if (!parsed || parsed.version !== CURRENT_VERSION) return DEFAULT_STATE;
    return {
      ...DEFAULT_STATE,
      ...parsed,
      categories: { ...DEFAULT_STATE.categories, ...parsed.categories, necessary: true },
    };
  } catch {
    return DEFAULT_STATE;
  }
}

export function setConsent(categories: Partial<Omit<ConsentCategories, "necessary">>) {
  if (typeof window === "undefined") return;
  const current = getConsent();
  const next: ConsentState = {
    categories: {
      necessary: true,
      analytics: categories.analytics ?? current.categories.analytics,
      marketing: categories.marketing ?? current.categories.marketing,
    },
    decided: true,
    timestamp: Date.now(),
    version: CURRENT_VERSION,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent<ConsentState>(CHANGE_EVENT, { detail: next }));
}

export function acceptAll() {
  setConsent({ analytics: true, marketing: true });
}

export function rejectAll() {
  setConsent({ analytics: false, marketing: false });
}

export function resetConsent() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent<ConsentState>(CHANGE_EVENT, { detail: DEFAULT_STATE }));
}

export function hasMarketingConsent() {
  return getConsent().categories.marketing === true;
}

export function hasAnalyticsConsent() {
  return getConsent().categories.analytics === true;
}

export function onConsentChange(handler: (state: ConsentState) => void) {
  if (typeof window === "undefined") return () => undefined;
  const listener = (ev: Event) => handler((ev as CustomEvent<ConsentState>).detail);
  window.addEventListener(CHANGE_EVENT, listener);
  return () => window.removeEventListener(CHANGE_EVENT, listener);
}

export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export function onOpenPreferences(handler: () => void) {
  if (typeof window === "undefined") return () => undefined;
  const listener = () => handler();
  window.addEventListener(OPEN_EVENT, listener);
  return () => window.removeEventListener(OPEN_EVENT, listener);
}

// Borra cookies de Meta/Facebook si el usuario revoca el consentimiento.
export function purgeMarketingCookies() {
  if (typeof document === "undefined") return;
  const names = ["_fbp", "_fbc", "fr"];
  const host = window.location.hostname;
  const rootDomain = host.split(".").slice(-2).join(".");
  names.forEach((name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${rootDomain}`;
  });
}
